import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { EMPTY, forkJoin, Subject } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil, tap, filter } from 'rxjs/operators';
import { ActionsService } from '../../../core/actions.service';
import { ApiService } from '../../../core/api.service';
import { AuthService } from '../../../core/auth.service';
import { Bill, Command, Room, Table, Waiter } from '../../../core/models';
import { ModalsService } from '../../../modals/modals.service';
import { ChangeTableModalComponent, ChangeTableModalData, ChangeTableModalReturn } from './change-table/change-table.component';
import { JoinTableModalComponent, JoinTableModalData, JoinTableModalReturn } from './join-table/join-table.component';
import { ChangeCommandModalComponent, ChangeCommandModalData, ChangeCommandModalReturn } from './change-command/change-command.component';
import { CancelCommandModalComponent, CancelCommandModalData, CancelCommandModalReturn } from './cancel-command/cancel-command.component';
import { DiscountModalComponent } from './discount/discount.component';
import { PayComponent } from './pay/pay.component';

@Component({
  templateUrl: 'bill.component.html'
})
export class BillModalComponent implements OnDestroy {

  static readonly defaultName: string = 'Cliente directo';

  private readonly destroyed: Subject<void> = new Subject<void>();

  private _loading: boolean;
  public get loading(): boolean {
    return this._loading;
  }
  public set loading(loading: boolean) {
    this._loading = loading;
    this.ref.disableClose = loading;
  }

  public waiters: Waiter[];
  public bill: Bill;

  public form: FormGroupTyped<{
    waiter: Waiter['idpvUsuarios'],
    adults: number,
    minors: number,
    name: string,
    room: string,
    guest: Room['idHotel']
  }> = new FormGroup({
    waiter: new FormControl(undefined, [Validators.required]),
    adults: new FormControl(0, [Validators.required, Validators.min(1)]),
    minors: new FormControl(0, [Validators.required, Validators.min(0)]),
    name: new FormControl(BillModalComponent.defaultName, [Validators.required]),
    room: new FormControl(undefined),
    guest: new FormControl(undefined),
  }) as any;

  constructor(
    private actions: ActionsService,
    private api: ApiService,
    public auth: AuthService,
    private dialog: MatDialog,
    private modals: ModalsService,
    private ref: MatDialogRef<BillModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { table: Table }
  ) {
    if (this.auth.user.permisos.cambiomesero) {
      this.form.controls.waiter.enable();
    } else {
      this.form.controls.waiter.disable();
    }


    this.form.controls.room.valueChanges.pipe(
      tap(() => {
        this.form.controls.guest.setValue(undefined);
        this.form.controls.name.setValue(BillModalComponent.defaultName);
        this.form.controls.name.enable();
      }),
      debounceTime(300),
      filter(value => !!value),
      switchMap(value => {
        return this.api.getRoom(value).pipe(
          catchError(() => EMPTY)
        );
      }),
      takeUntil(this.destroyed)
    ).subscribe({
      next: room => {
        this.form.controls.guest.setValue(room.idHotel);
        this.form.controls.name.setValue(room.huesped);
        this.form.controls.name.disable();
      }
    });

    this.loadBill();
  }

  public loadBill(): void {
    this.loading = true;
    forkJoin(
      this.api.getWaiters(),
      this.api.getBill(this.data.table.idpvVentas)
    ).pipe(
      takeUntil(this.destroyed)
    ).subscribe({
      next: ([waiters, bill]) => {
        this.waiters = waiters;
        this.bill = bill;
        this.form.patchValue({
          waiter: bill.idpvUsuariosMesero,
          adults: bill.adultos,
          minors: bill.menores,
          name: bill.nombre,
          guest: bill.idHotel,
          room: bill.habitacion,
        });
        this.loading = false;
      },
      error: error => {
        console.error(error);
        this.loading = false;
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  public pay(): void {
    this.dialog.open(PayComponent, {
      data: { table: this.data.table, bill: this.bill }
    }).afterClosed().pipe(
      takeUntil(this.destroyed)
    ).subscribe(result => {
      if (result) {
        this.ref.close();
      }
    });
  }

  public discount(): void {
    this.dialog.open(DiscountModalComponent, {
      data: {
        table: this.data.table,
        bill: this.bill
      }
    }).afterClosed().pipe(
      takeUntil(this.destroyed)
    ).subscribe(result => {
      if (result) {
        this.loadBill();
      }
    });
  }

  public changeSub(command: Command, sub: number): void {
    this.loading = true;
    this.api.changeSub(command, sub).subscribe(
      () => {
        this.loading = false;
        this.loadBill();
      },
      error => {
        console.error(error);
        this.loading = false;
        this.loadBill();
      }
    );
  }

  public changeTable(): void {
    this.dialog.open<ChangeTableModalComponent, ChangeTableModalData, ChangeTableModalReturn>(
      ChangeTableModalComponent, {
      data: {
        table: this.data.table
      }
    }
    ).afterClosed().subscribe({
      next: changed => {
        if (changed) {
          this.loadBill();
        }
      }
    });
  }

  public joinTable(): void {
    this.dialog.open<JoinTableModalComponent, JoinTableModalData, JoinTableModalReturn>(
      JoinTableModalComponent, {
      data: {
        table: this.data.table
      }
    }
    ).afterClosed().subscribe({
      next: changed => {
        if (changed) {
          this.loadBill();
        }
      }
    });
  }

  public async separateTable() {
    if (await this.modals.alert({
      title: 'Separar mesas',
      message: '¿Esta seguro de que desea separar la mesa?',
      ok: 'Si',
      cancel: 'No'
    })) {
      this.loading = true;
      this.api.separateTable(this.data.table).subscribe({
        next: () => {
          this.loading = false;
          this.loadBill();
        },
        error: error => {
          console.error(error);
          this.loading = false;
          this.loadBill();
        }
      });
    }
  }

  public async cancelCommand(command: Command): Promise<void> {
    this.dialog.open<CancelCommandModalComponent, CancelCommandModalData, CancelCommandModalReturn>(
      CancelCommandModalComponent, {
      data: {
        command: command,
      }
    }
    ).afterClosed().subscribe({
      next: changed => {
        if (changed) {
          this.loadBill();
        }
      }
    });
  }

  public async printOrder(command: Command): Promise<void> {
    if (await this.modals.alert({
      title: 'Reimprimir comanda',
      message: '¿Esta seguro de que desea reimprimir la comanda?',
      ok: 'Si',
      cancel: 'No'
    })) {
      this.loading = true;
      this.api.printOrder(command.idpvVentas, command.folio).subscribe({
        next: () => {
          this.loading = false;
        },
        error: error => {
          console.error(error);
          this.loading = false;
        }
      });
    }
  }

  public async moveOrder(command: Command): Promise<void> {
    this.dialog.open<ChangeCommandModalComponent, ChangeCommandModalData, ChangeCommandModalReturn>(
      ChangeCommandModalComponent, {
      data: {
        table: this.data.table,
        command: command,
      }
    }
    ).afterClosed().subscribe({
      next: changed => {
        if (changed) {
          this.loadBill();
        }
      }
    });
  }

  public async order(): Promise<void> {
    this.ref.close();
    this.actions.order(this.data.table.idpvAreasMesas);
  }

  public async printCheck(): Promise<void> {
    if (await this.modals.alert({
      title: 'Imprimir cheque',
      message: '¿Esta seguro de que desea imprimir cheque?',
      ok: 'Si',
      cancel: 'No'
    })) {
      this.loading = true;
      this.api.printCheck(this.bill.idpvVentas).subscribe({
        next: () => {
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
    }
  }

  public cancel(): void {
    this.ref.close();
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    this.api.updateBill({
      idpvVentas: this.data.table.idpvVentas,
      idpvUsuariosMesero: this.form.controls.waiter.value,
      adultos: this.form.controls.adults.value,
      menores: this.form.controls.minors.value,
      nombre: this.form.controls.name.value,
      idHotel: this.form.controls.guest.value,
      habitacion: this.form.value.guest ? this.form.value.room : undefined
    }).pipe(
      takeUntil(this.destroyed)
    ).subscribe(
      () => {
        this.ref.close();
        this.loading = false;
      },
      error => {
        console.log(error);
        this.loading = false;
      }
    );
  }

}
